import { useCallback, useState } from "react";

import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";

import { useTranslation } from "@shared/utils/internationalization";
import { Timer } from "@ui-kit/feedback/timer";
import Button from "@ui-kit/form/Button/Button.web";

import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useAllowAddMoreCartTime } from "./hooks/useAllowAddMoreCartTime";

/**
 *
 * Used on the public checkout.
 * Uses useBasketService to manage basket expiry and time extension functionality.
 * Includes the Add More Time button.
 *
 * separate file to allow for dynamic import
 *  */
export default function CartTimerCheckout() {
  const { t } = useTranslation("common");

  const { expiryDate, hasItems, isBasketUpdating, paymentVenue, extendBasket } =
    useBasketService<IBasket>();

  // only allow adding more time to the cart if the timer is under 2 minutes
  // and the venue allows baskets to be extended
  const allowAddMoreTime = useAllowAddMoreCartTime(expiryDate, paymentVenue);

  const [isButtonFocused, setIsButtonFocused] = useState(false);

  const handleFocus = useCallback(() => {
    setIsButtonFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsButtonFocused(false);
  }, []);

  // Conditionally set the simple aria-label only when focused
  const buttonAriaLabel = isButtonFocused
    ? t("Add more time button") // Simple label when focused
    : undefined; // Allow children content to form the label when not focused

  return expiryDate && hasItems ? (
    <Flex
      data-testid="cart-timer"
      height="element.2xl"
      alignItems="center"
      w="full"
    >
      {/* Show "X Time left" text */}
      {!allowAddMoreTime && (
        <Flex
          data-testid="cart-timer-text"
          color="red.solid"
          fontSize="md"
          alignItems="center"
          gap="2xs"
          tabIndex={0}
        >
          <Text aria-hidden="true">{t("Time left")}</Text>
          <Text>
            <Timer
              targetDate={expiryDate}
              announcementPrefix={t("Time left")}
            />
          </Text>
        </Flex>
      )}

      {/* Show "X More time" button for adding more time to the cart */}
      {allowAddMoreTime && (
        <Button.Outline
          data-testid="cart-timer-button"
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={buttonAriaLabel}
          size="sm"
          colorPalette="red"
          variant="outline"
          onClick={extendBasket}
          isLoading={isBasketUpdating}
        >
          <Flex fontSize="md" fontWeight="normal">
            <Text mr="2xs">
              <Timer targetDate={expiryDate} />
            </Text>
            <Text>{t("More time")}</Text>
          </Flex>
        </Button.Outline>
      )}
    </Flex>
  ) : (
    <></>
  );
}
